import { Component } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { SuccessDialogComponent } from "../../../shared/dialogs/success-dialog/success-dialog.component";
import { ErrorDialogComponent } from "../../../shared/dialogs/error-dialog/error-dialog.component";
import {
  AssetLossFactors,
  ExternalLossFactors,
  LossMagnitude,
  LossEventFrequency,
  OrganizationalLossFactors,
  RiskRegisterAsset,
  RiskRegister,
  RiskRegisterOrganization,
  RiskRegisterService,
  ThreatEventFrequency,
  ThreatLossFactors,
  Vulnerability,
} from "../risk-register-service";

@Component({
  selector: "app-risk-register-create-or-update",
  templateUrl: "./risk-register-create-or-update.component.html",
  styleUrls: ["./risk-register-create-or-update.component.scss"],
})
export class RiskRegisterCreateOrUpdateComponent {
  currentStep = 1;
  isCreate = false;
  isStepFourSaving = false;
  isStepFiveSaving = false;
  isStepSixSaving = false;
  isStepSevenSaving = false;
  isStepEightSaving = false;
  isStepNineSaving = false;
  isStepTenSaving = false;
  riskRegisterId = "";
  tefId = "";
  vulnerabilityId = "";
  lefId = "";
  alfId = "";
  tlfId = "";
  olfId = "";
  elfId = "";
  lmId = "";
  organizationId = "";
  organizations: RiskRegisterOrganization[] = [];
  assets: RiskRegisterAsset[] = [];
  filteredAssets: RiskRegisterAsset[] = [];

  readonly statusOptions = ["DRAFT", "ACTIVE"];
  readonly timeframeOptions = ["Year", "Quarter", "Month"];
  readonly threatActionOptions = [
    "Access",
    "Modify",
    "Disclose",
    "Misuse",
    "Deny",
  ];
  readonly threatOriginOptions = ["Internal", "External", "Third Party"];
  readonly threatActionWeights: Record<string, number> = {
    Access: 1,
    Modify: 2,
    Disclose: 3,
    Misuse: 4,
    Deny: 5,
  };
  readonly threatOriginMultipliers: Record<string, number> = {
    Internal: 1,
    External: 1.2,
    "Third Party": 1.1,
  };

  stepOneForm = new FormGroup({
    organizationId: new FormControl("", Validators.required),
    assetId: new FormControl("", Validators.required),
    assessmentName: new FormControl("", Validators.required),
    description: new FormControl("", Validators.required),
    status: new FormControl("DRAFT", Validators.required),
  });

  stepTwoForm = new FormGroup({
    contactFrequency: new FormControl(""),
    probabilityOfAction: new FormControl(""),
    timeframe: new FormControl("Year"),
    threatAgent: new FormControl(""),
    tefResult: new FormControl({ value: "", disabled: true }),
  });

  stepThreeForm = new FormGroup({
    method: new FormControl("Derived"),
    threatCapability: new FormControl(""),
    resistanceStrength: new FormControl(""),
    vulnerabilityResult: new FormControl({ value: "", disabled: true }),
  });

  stepFourForm = new FormGroup({
    tef: new FormControl({ value: "", disabled: true }),
    vulnerability: new FormControl({ value: "", disabled: true }),
    timeframe: new FormControl({ value: "Year", disabled: true }),
    lefResult: new FormControl({ value: "", disabled: true }),
  });

  stepFiveForm = new FormGroup({
    criticality: new FormControl("3"),
    replacementCost: new FormControl(""),
    recoveryCost: new FormControl(""),
    rebuildCost: new FormControl(""),
    sensitivityScore: new FormControl("3"),
    reputationScore: new FormControl("3"),
    legalExposure: new FormControl(""),
    competitiveValue: new FormControl(""),
    volume: new FormControl(""),
  });

  stepSixForm = new FormGroup({
    threatAction: new FormControl("Access"),
    threatCompetence: new FormControl("3"),
    threatOrigin: new FormControl("External"),
    tlfScore: new FormControl({ value: "", disabled: true }),
  });

  stepSevenForm = new FormGroup({
    timingScore: new FormControl("3"),
    careScore: new FormControl("3"),
    detectionScore: new FormControl("3"),
    containmentScore: new FormControl("3"),
    remediationScore: new FormControl("3"),
    recoveryScore: new FormControl("3"),
    olfScore: new FormControl({ value: "", disabled: true }),
  });

  stepEightForm = new FormGroup({
    externalDetection: new FormControl(""),
    regulatoryExposure: new FormControl(""),
    competitorScore: new FormControl("3"),
    mediaScore: new FormControl("3"),
    stakeholderScore: new FormControl("3"),
    elfScore: new FormControl({ value: "", disabled: true }),
  });

  stepNineForm = new FormGroup({
    productivityLoss: new FormControl(""),
    responseCost: new FormControl(""),
    replacementCost: new FormControl(""),
    fines: new FormControl(""),
    competitiveLoss: new FormControl(""),
    reputationLoss: new FormControl(""),
    primaryLoss: new FormControl({ value: "", disabled: true }),
    secondaryLoss: new FormControl({ value: "", disabled: true }),
    totalLossMagnitude: new FormControl({ value: "", disabled: true }),
    currency: new FormControl("USD"),
  });

  stepTenForm = new FormGroup({
    lef: new FormControl({ value: "", disabled: true }),
    lossMagnitude: new FormControl({ value: "", disabled: true }),
    risk: new FormControl({ value: "", disabled: true }),
  });

  constructor(
    private route: ActivatedRoute,
    private riskRegisterService: RiskRegisterService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.riskRegisterId = this.route.snapshot.params.riskRegisterId;
    this.registerTefCalculation();
    this.registerVulnerabilityCalculation();
    this.registerLefCalculation();
    this.registerTlfCalculation();
    this.registerOlfCalculation();
    this.registerElfCalculation();
    this.registerLossMagnitudeCalculation();
    this.registerRiskCalculation();
    this.applyDefaultDerivedResults();
    await this.loadOrganizations();
    await this.loadAssets();

    if (!this.isCreate && this.riskRegisterId) {
      await this.loadRiskAssessment();
      await this.loadThreatEventFrequency();
      await this.loadVulnerability();
      await this.loadLossEventFrequency();
      await this.loadAssetLossFactors();
      await this.loadThreatLossFactors();
      await this.loadOrganizationalLossFactors();
      await this.loadExternalLossFactors();
      await this.loadLossMagnitude();
    }
  }

  private applyDefaultDerivedResults(): void {
    this.stepFourForm.controls.timeframe.setValue(
      this.stepTwoForm.controls.timeframe.value ?? "Year",
      { emitEvent: false },
    );
    this.updateTlfResult();
    this.updateOlfResult();
    this.updateElfResult();
  }

  async loadOrganizations(): Promise<void> {
    try {
      const organizations =
        (await this.riskRegisterService.getOrganizations().toPromise()) ?? [];
      this.organizations = organizations;

      const selectedOrganization = organizations.find(
        (organization) => organization.organizationId === this.organizationId,
      );

      if (selectedOrganization) {
        this.organizationId = selectedOrganization.organizationId;
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
    }
  }

  async loadAssets(): Promise<void> {
    try {
      const assets =
        (await this.riskRegisterService
          .getAssets(this.organizationId)
          .toPromise()) ?? [];
      this.assets = assets;
      this.applyOrganizationAssetFilter();
    } catch (error) {
      console.error("Error loading assets:", error);
    }
  }

  async loadRiskAssessment(): Promise<void> {
    try {
      const assessment = await this.riskRegisterService
        .getRiskRegisters(this.riskRegisterId)
        .toPromise();

      if (!assessment) {
        return;
      }

      this.stepOneForm.patchValue({
        organizationId: assessment.organizationId ?? this.organizationId,
        assetId: assessment.assetId ?? "",
        assessmentName: assessment.assessmentName ?? "",
        description: assessment.description ?? "",
        status: assessment.status ?? "DRAFT",
      });
      this.stepTenForm.patchValue({
        risk: assessment.risk != null ? `${assessment.risk}` : "",
      });
      this.organizationId = assessment.organizationId ?? this.organizationId;
      this.applyOrganizationAssetFilter();
    } catch (error) {
      console.error("Error loading risk assessment:", error);
    }
  }

  onOrganizationChange(): void {
    this.organizationId = this.stepOneForm.controls.organizationId.value ?? "";
    this.stepOneForm.patchValue({ assetId: "" });
    void this.loadAssets();
  }

  onAssetChange(): void {
    return;
  }

  get selectedAssetName(): string {
    return (
      this.filteredAssets.find(
        (asset) => asset.assetId === this.stepOneForm.controls.assetId.value,
      )?.assetName ?? "Not selected"
    );
  }

  get selectedOrganizationName(): string {
    return (
      this.organizations.find(
        (organization) =>
          organization.organizationId ===
          this.stepOneForm.controls.organizationId.value,
      )?.organizationName ?? "Not selected"
    );
  }

  private applyOrganizationAssetFilter(): void {
    const organizationId =
      this.stepOneForm.controls.organizationId.value ?? this.organizationId;

    this.filteredAssets = this.assets.filter(
      (asset) => asset.organizationId === organizationId,
    );

    const currentAssetId = this.stepOneForm.controls.assetId.value;
    const currentAssetStillValid = this.filteredAssets.some(
      (asset) => asset.assetId === currentAssetId,
    );

    const selectedAsset = currentAssetStillValid
      ? this.filteredAssets.find((asset) => asset.assetId === currentAssetId)
      : null;

    if (!selectedAsset) {
      return;
    }

    this.stepOneForm.patchValue({
      assetId: selectedAsset.assetId,
    });
  }

  async loadThreatEventFrequency(): Promise<void> {
    try {
      const threatEventFrequency = await this.riskRegisterService
        .getThreatEventFrequency(this.riskRegisterId)
        .toPromise();

      if (!threatEventFrequency) {
        return;
      }

      this.tefId = threatEventFrequency.tefId ?? "";
      this.stepTwoForm.patchValue({
        contactFrequency:
          threatEventFrequency.contactFrequency != null
            ? `${threatEventFrequency.contactFrequency}`
            : "",
        probabilityOfAction:
          threatEventFrequency.probabilityOfAction != null
            ? `${threatEventFrequency.probabilityOfAction}`
            : "",
        timeframe: threatEventFrequency.timeframe ?? "",
        threatAgent: threatEventFrequency.threatAgent ?? "",
        tefResult:
          threatEventFrequency.tefResult != null
            ? `${threatEventFrequency.tefResult}`
            : "",
      });
    } catch (error) {
      console.error("Error loading threat event frequency:", error);
    }
  }

  async loadVulnerability(): Promise<void> {
    try {
      const vulnerability = await this.riskRegisterService
        .getVulnerability(this.riskRegisterId)
        .toPromise();

      if (!vulnerability) {
        return;
      }

      this.vulnerabilityId = vulnerability.vulnerabilityId ?? "";
      this.stepThreeForm.patchValue({
        method: vulnerability.method ?? "Derived",
        threatCapability:
          vulnerability.threatCapability != null
            ? `${vulnerability.threatCapability}`
            : "",
        resistanceStrength:
          vulnerability.resistanceStrength != null
            ? `${vulnerability.resistanceStrength}`
            : "",
        vulnerabilityResult:
          vulnerability.vulnerabilityResult != null
            ? `${vulnerability.vulnerabilityResult}`
            : "",
      });
    } catch (error) {
      console.error("Error loading vulnerability:", error);
    }
  }

  async loadLossEventFrequency(): Promise<void> {
    try {
      const lossEventFrequency = await this.riskRegisterService
        .getLossEventFrequency(this.riskRegisterId)
        .toPromise();

      if (!lossEventFrequency) {
        return;
      }

      this.lefId = lossEventFrequency.lefId ?? "";
      this.stepFourForm.patchValue({
        tef:
          lossEventFrequency.tef != null
            ? `${lossEventFrequency.tef}`
            : "",
        vulnerability:
          lossEventFrequency.vulnerability != null
            ? `${lossEventFrequency.vulnerability}`
            : "",
        timeframe:
          lossEventFrequency.timeframe ??
          this.stepTwoForm.controls.timeframe.value ??
          "",
        lefResult:
          lossEventFrequency.lefResult != null
            ? `${lossEventFrequency.lefResult}`
            : "",
      });
    } catch (error) {
      console.error("Error loading loss event frequency:", error);
    }
  }

  async loadAssetLossFactors(): Promise<void> {
    try {
      const assetLossFactors = await this.riskRegisterService
        .getAssetLossFactors(this.riskRegisterId)
        .toPromise();

      if (!assetLossFactors) {
        return;
      }

      this.alfId = assetLossFactors.alfId ?? "";
      this.stepFiveForm.patchValue({
        criticality:
          assetLossFactors.criticality != null
            ? `${assetLossFactors.criticality}`
            : "",
        replacementCost:
          assetLossFactors.replacementCost != null
            ? `${assetLossFactors.replacementCost}`
            : "",
        recoveryCost:
          assetLossFactors.recoveryCost != null
            ? `${assetLossFactors.recoveryCost}`
            : "",
        rebuildCost:
          assetLossFactors.rebuildCost != null
            ? `${assetLossFactors.rebuildCost}`
            : "",
        sensitivityScore:
          assetLossFactors.sensitivityScore != null
            ? `${assetLossFactors.sensitivityScore}`
            : "",
        reputationScore:
          assetLossFactors.reputationScore != null
            ? `${assetLossFactors.reputationScore}`
            : "",
        legalExposure:
          assetLossFactors.legalExposure != null
            ? `${assetLossFactors.legalExposure}`
            : "",
        competitiveValue:
          assetLossFactors.competitiveValue != null
            ? `${assetLossFactors.competitiveValue}`
            : "",
        volume:
          assetLossFactors.volume != null ? `${assetLossFactors.volume}` : "",
      });
    } catch (error) {
      console.error("Error loading asset loss factors:", error);
    }
  }

  async loadThreatLossFactors(): Promise<void> {
    try {
      const threatLossFactors = await this.riskRegisterService
        .getThreatLossFactors(this.riskRegisterId)
        .toPromise();

      if (!threatLossFactors) {
        return;
      }

      this.tlfId = threatLossFactors.tlfId ?? "";
      this.stepSixForm.patchValue({
        threatAction: threatLossFactors.threatAction ?? "",
        threatCompetence:
          threatLossFactors.threatCompetence != null
            ? `${threatLossFactors.threatCompetence}`
            : "",
        threatOrigin: threatLossFactors.threatOrigin ?? "",
        tlfScore:
          threatLossFactors.tlfScore != null
            ? `${threatLossFactors.tlfScore}`
            : "",
      });
    } catch (error) {
      console.error("Error loading threat loss factors:", error);
    }
  }

  async loadOrganizationalLossFactors(): Promise<void> {
    try {
      const organizationalLossFactors = await this.riskRegisterService
        .getOrganizationalLossFactors(this.riskRegisterId)
        .toPromise();

      if (!organizationalLossFactors) {
        return;
      }

      this.olfId = organizationalLossFactors.olfId ?? "";
      this.stepSevenForm.patchValue({
        timingScore:
          organizationalLossFactors.timingScore != null
            ? `${organizationalLossFactors.timingScore}`
            : "",
        careScore:
          organizationalLossFactors.careScore != null
            ? `${organizationalLossFactors.careScore}`
            : "",
        detectionScore:
          organizationalLossFactors.detectionScore != null
            ? `${organizationalLossFactors.detectionScore}`
            : "",
        containmentScore:
          organizationalLossFactors.containmentScore != null
            ? `${organizationalLossFactors.containmentScore}`
            : "",
        remediationScore:
          organizationalLossFactors.remediationScore != null
            ? `${organizationalLossFactors.remediationScore}`
            : "",
        recoveryScore:
          organizationalLossFactors.recoveryScore != null
            ? `${organizationalLossFactors.recoveryScore}`
            : "",
        olfScore:
          organizationalLossFactors.olfScore != null
            ? `${organizationalLossFactors.olfScore}`
            : "",
      });
    } catch (error) {
      console.error("Error loading organizational loss factors:", error);
    }
  }

  async loadExternalLossFactors(): Promise<void> {
    try {
      const externalLossFactors = await this.riskRegisterService
        .getExternalLossFactors(this.riskRegisterId)
        .toPromise();

      if (!externalLossFactors) {
        return;
      }

      this.elfId = externalLossFactors.elfId ?? "";
      this.stepEightForm.patchValue({
        externalDetection:
          externalLossFactors.externalDetection != null
            ? `${externalLossFactors.externalDetection}`
            : "",
        regulatoryExposure:
          externalLossFactors.regulatoryExposure != null
            ? `${externalLossFactors.regulatoryExposure}`
            : "",
        competitorScore:
          externalLossFactors.competitorScore != null
            ? `${externalLossFactors.competitorScore}`
            : "",
        mediaScore:
          externalLossFactors.mediaScore != null
            ? `${externalLossFactors.mediaScore}`
            : "",
        stakeholderScore:
          externalLossFactors.stakeholderScore != null
            ? `${externalLossFactors.stakeholderScore}`
            : "",
        elfScore:
          externalLossFactors.elfScore != null
            ? `${externalLossFactors.elfScore}`
            : "",
      });
    } catch (error) {
      console.error("Error loading external loss factors:", error);
    }
  }


  async loadLossMagnitude(): Promise<void> {
    try {
      const lossMagnitude = await this.riskRegisterService
        .getLossMagnitude(this.riskRegisterId)
        .toPromise();

      if (!lossMagnitude) {
        return;
      }

      this.lmId = lossMagnitude.lmId ?? "";
      this.stepNineForm.patchValue({
        productivityLoss:
          lossMagnitude.productivityLoss != null
            ? `${lossMagnitude.productivityLoss}`
            : "",
        responseCost:
          lossMagnitude.responseCost != null
            ? `${lossMagnitude.responseCost}`
            : "",
        replacementCost:
          lossMagnitude.replacementCost != null
            ? `${lossMagnitude.replacementCost}`
            : "",
        fines: lossMagnitude.fines != null ? `${lossMagnitude.fines}` : "",
        competitiveLoss:
          lossMagnitude.competitiveLoss != null
            ? `${lossMagnitude.competitiveLoss}`
            : "",
        reputationLoss:
          lossMagnitude.reputationLoss != null
            ? `${lossMagnitude.reputationLoss}`
            : "",
        primaryLoss:
          lossMagnitude.primaryLoss != null
            ? `${lossMagnitude.primaryLoss}`
            : "",
        secondaryLoss:
          lossMagnitude.secondaryLoss != null
            ? `${lossMagnitude.secondaryLoss}`
            : "",
        totalLossMagnitude:
          lossMagnitude.totalLossMagnitude != null
            ? `${lossMagnitude.totalLossMagnitude}`
            : "",
        currency: lossMagnitude.currency ?? "USD",
      });
    } catch (error) {
      console.error("Error loading loss magnitude:", error);
    }
  }

  async saveStepOne(): Promise<void> {
    if (this.stepOneForm.invalid) {
      this.stepOneForm.markAllAsTouched();
      return;
    }

    this.currentStep = 2;
  }

  goToStep(step: number): void {
    this.currentStep = step;
  }

  async saveStepTwo(): Promise<void> {
    this.currentStep = 3;
  }

  async saveStepThree(): Promise<void> {
    this.currentStep = 4;
  }

  async saveStepFour(): Promise<void> {
    this.currentStep = 5;
  }

  async saveStepFive(): Promise<void> {
    this.currentStep = 6;
  }

  async saveStepSix(): Promise<void> {
    this.currentStep = 7;
  }

  async saveStepSeven(): Promise<void> {
    this.currentStep = 8;
  }

  async saveStepEight(): Promise<void> {
    this.currentStep = 9;
  }

  async saveStepNine(): Promise<void> {
    this.currentStep = 10;
  }

  async saveStepTen(): Promise<void> {
    if (this.isStepTenSaving) {
      return;
    }

    try {
      this.isStepTenSaving = true;
      const isCreateMode = this.isCreate;
      const organizationId =
        this.stepOneForm.controls.organizationId.value ?? this.organizationId;

      const riskPayload = this.buildRiskRegisterPayload();
      if (this.isCreate) {
        const response = await this.riskRegisterService
          .createRiskRegister(riskPayload)
          .toPromise();
        const createdAssessmentId =
          response?.assessmentId ?? response?.assessment_id ?? "";
        if (!createdAssessmentId) {
          throw new Error("Assessment id was not returned.");
        }
        this.riskRegisterId = createdAssessmentId;
        this.isCreate = false;
      } else {
        await this.riskRegisterService
          .updateRiskRegister(this.riskRegisterId, riskPayload)
          .toPromise();
      }

      const tefPayload = this.buildThreatEventFrequencyPayload(organizationId);
      if (this.hasThreatEventFrequencyData(tefPayload)) {
        if (this.tefId) {
          await this.riskRegisterService
            .updateThreatEventFrequency(this.tefId, tefPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createThreatEventFrequency(tefPayload)
            .toPromise();
          this.tefId = response?.tefId ?? response?.tef_id ?? "";
        }
      }

      const vulnerabilityPayload = this.buildVulnerabilityPayload(organizationId);
      if (this.hasVulnerabilityData(vulnerabilityPayload)) {
        if (this.vulnerabilityId) {
          await this.riskRegisterService
            .updateVulnerability(this.vulnerabilityId, vulnerabilityPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createVulnerability(vulnerabilityPayload)
            .toPromise();
          this.vulnerabilityId =
            response?.vulnerabilityId ?? response?.vulnerability_id ?? "";
        }
      }

      const lefPayload = this.buildLossEventFrequencyPayload(organizationId);
      if (this.hasLossEventFrequencyData(lefPayload)) {
        if (this.lefId) {
          await this.riskRegisterService
            .updateLossEventFrequency(this.lefId, lefPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createLossEventFrequency(lefPayload)
            .toPromise();
          this.lefId = response?.lefId ?? response?.lef_id ?? "";
        }
      }

      const alfPayload = this.buildAssetLossFactorsPayload(organizationId);
      if (this.hasAssetLossFactorsData(alfPayload)) {
        if (this.alfId) {
          await this.riskRegisterService
            .updateAssetLossFactors(this.alfId, alfPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createAssetLossFactors(alfPayload)
            .toPromise();
          this.alfId = response?.alfId ?? response?.alf_id ?? "";
        }
      }

      const tlfPayload = this.buildThreatLossFactorsPayload(organizationId);
      if (this.hasThreatLossFactorsData(tlfPayload)) {
        if (this.tlfId) {
          await this.riskRegisterService
            .updateThreatLossFactors(this.tlfId, tlfPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createThreatLossFactors(tlfPayload)
            .toPromise();
          this.tlfId = response?.tlfId ?? response?.tlf_id ?? "";
        }
      }

      const olfPayload = this.buildOrganizationalLossFactorsPayload(
        organizationId,
      );
      if (this.hasOrganizationalLossFactorsData(olfPayload)) {
        if (this.olfId) {
          await this.riskRegisterService
            .updateOrganizationalLossFactors(this.olfId, olfPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createOrganizationalLossFactors(olfPayload)
            .toPromise();
          this.olfId = response?.olfId ?? response?.olf_id ?? "";
        }
      }

      const elfPayload = this.buildExternalLossFactorsPayload(organizationId);
      if (this.hasExternalLossFactorsData(elfPayload)) {
        if (this.elfId) {
          await this.riskRegisterService
            .updateExternalLossFactors(this.elfId, elfPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createExternalLossFactors(elfPayload)
            .toPromise();
          this.elfId = response?.elfId ?? response?.elf_id ?? "";
        }
      }

      const lmPayload = this.buildLossMagnitudePayload(organizationId);
      if (this.hasLossMagnitudeData(lmPayload)) {
        if (this.lmId) {
          await this.riskRegisterService
            .updateLossMagnitude(this.lmId, lmPayload)
            .toPromise();
        } else {
          const response = await this.riskRegisterService
            .createLossMagnitude(lmPayload)
            .toPromise();
          this.lmId = response?.lmId ?? response?.lm_id ?? "";
        }
      }

      this.openSuccessDialog(isCreateMode);
    } catch (error: any) {
      this.openErrorDialog(
        error?.message || "Unable to save the calculator values.",
      );
    } finally {
      this.isStepTenSaving = false;
    }
  }

  private buildRiskRegisterPayload(): Partial<RiskRegister> {
    const formValue = this.stepOneForm.getRawValue();
    return {
      organizationId: formValue.organizationId ?? this.organizationId,
      assetId: formValue.assetId ?? undefined,
      assessmentName: formValue.assessmentName ?? undefined,
      description: formValue.description ?? undefined,
      status: formValue.status ?? undefined,
      risk: this.parseOptionalNumber(this.stepTenForm.controls.risk.value),
    };
  }

  private buildThreatEventFrequencyPayload(
    organizationId: string,
  ): Partial<ThreatEventFrequency> {
    const formValue = this.stepTwoForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      contactFrequency: this.parseOptionalNumber(formValue.contactFrequency),
      probabilityOfAction: this.parseOptionalNumber(
        formValue.probabilityOfAction,
      ),
      timeframe: formValue.timeframe?.trim() || undefined,
      threatAgent: formValue.threatAgent?.trim() || undefined,
      tefResult: this.parseOptionalNumber(formValue.tefResult),
    };
  }

  private buildVulnerabilityPayload(
    organizationId: string,
  ): Partial<Vulnerability> {
    const formValue = this.stepThreeForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      method: formValue.method?.trim() || "Derived",
      threatCapability: this.parseOptionalNumber(formValue.threatCapability),
      resistanceStrength: this.parseOptionalNumber(
        formValue.resistanceStrength,
      ),
      vulnerabilityResult: this.parseOptionalNumber(
        formValue.vulnerabilityResult,
      ),
    };
  }

  private buildLossEventFrequencyPayload(
    organizationId: string,
  ): Partial<LossEventFrequency> {
    const formValue = this.stepFourForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      tef: this.parseOptionalNumber(formValue.tef),
      vulnerability: this.parseOptionalNumber(formValue.vulnerability),
      timeframe: formValue.timeframe?.trim() || undefined,
      lefResult: this.parseOptionalNumber(formValue.lefResult),
    };
  }

  private buildAssetLossFactorsPayload(
    organizationId: string,
  ): Partial<AssetLossFactors> {
    const formValue = this.stepFiveForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      criticality: this.parseOptionalNumber(formValue.criticality),
      replacementCost: this.parseOptionalNumber(formValue.replacementCost),
      recoveryCost: this.parseOptionalNumber(formValue.recoveryCost),
      rebuildCost: this.parseOptionalNumber(formValue.rebuildCost),
      sensitivityScore: this.parseOptionalNumber(formValue.sensitivityScore),
      reputationScore: this.parseOptionalNumber(formValue.reputationScore),
      legalExposure: this.parseOptionalNumber(formValue.legalExposure),
      competitiveValue: this.parseOptionalNumber(formValue.competitiveValue),
      volume: this.parseOptionalNumber(formValue.volume),
    };
  }

  private buildThreatLossFactorsPayload(
    organizationId: string,
  ): Partial<ThreatLossFactors> {
    const formValue = this.stepSixForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      threatAction: formValue.threatAction?.trim() || undefined,
      threatCompetence: this.parseOptionalNumber(formValue.threatCompetence),
      threatOrigin: formValue.threatOrigin?.trim() || undefined,
      tlfScore: this.parseOptionalNumber(formValue.tlfScore),
    };
  }

  private buildOrganizationalLossFactorsPayload(
    organizationId: string,
  ): Partial<OrganizationalLossFactors> {
    const formValue = this.stepSevenForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      timingScore: this.parseOptionalNumber(formValue.timingScore),
      careScore: this.parseOptionalNumber(formValue.careScore),
      detectionScore: this.parseOptionalNumber(formValue.detectionScore),
      containmentScore: this.parseOptionalNumber(formValue.containmentScore),
      remediationScore: this.parseOptionalNumber(formValue.remediationScore),
      recoveryScore: this.parseOptionalNumber(formValue.recoveryScore),
      olfScore: this.parseOptionalNumber(formValue.olfScore),
    };
  }

  private buildExternalLossFactorsPayload(
    organizationId: string,
  ): Partial<ExternalLossFactors> {
    const formValue = this.stepEightForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      externalDetection: this.parseOptionalNumber(formValue.externalDetection),
      regulatoryExposure: this.parseOptionalNumber(formValue.regulatoryExposure),
      competitorScore: this.parseOptionalNumber(formValue.competitorScore),
      mediaScore: this.parseOptionalNumber(formValue.mediaScore),
      stakeholderScore: this.parseOptionalNumber(formValue.stakeholderScore),
      elfScore: this.parseOptionalNumber(formValue.elfScore),
    };
  }

  private buildLossMagnitudePayload(
    organizationId: string,
  ): Partial<LossMagnitude> {
    const formValue = this.stepNineForm.getRawValue();
    return {
      assessmentId: this.riskRegisterId,
      organizationId,
      productivityLoss: this.parseOptionalNumber(formValue.productivityLoss),
      responseCost: this.parseOptionalNumber(formValue.responseCost),
      replacementCost: this.parseOptionalNumber(formValue.replacementCost),
      fines: this.parseOptionalNumber(formValue.fines),
      competitiveLoss: this.parseOptionalNumber(formValue.competitiveLoss),
      reputationLoss: this.parseOptionalNumber(formValue.reputationLoss),
      primaryLoss: this.parseOptionalNumber(formValue.primaryLoss),
      secondaryLoss: this.parseOptionalNumber(formValue.secondaryLoss),
      totalLossMagnitude: this.parseOptionalNumber(formValue.totalLossMagnitude),
      currency: formValue.currency?.trim() || "USD",
    };
  }

  private hasThreatEventFrequencyData(
    payload: Partial<ThreatEventFrequency>,
  ): boolean {
    return (
      payload.contactFrequency != null ||
      payload.probabilityOfAction != null ||
      !!payload.timeframe ||
      !!payload.threatAgent ||
      payload.tefResult != null
    );
  }

  private hasVulnerabilityData(payload: Partial<Vulnerability>): boolean {
    return (
      !!payload.method ||
      payload.threatCapability != null ||
      payload.resistanceStrength != null ||
      payload.vulnerabilityResult != null
    );
  }

  private hasLossEventFrequencyData(
    payload: Partial<LossEventFrequency>,
  ): boolean {
    return (
      payload.tef != null ||
      payload.vulnerability != null ||
      !!payload.timeframe ||
      payload.lefResult != null
    );
  }

  private hasAssetLossFactorsData(
    payload: Partial<AssetLossFactors>,
  ): boolean {
    return (
      payload.criticality != null ||
      payload.replacementCost != null ||
      payload.recoveryCost != null ||
      payload.rebuildCost != null ||
      payload.sensitivityScore != null ||
      payload.reputationScore != null ||
      payload.legalExposure != null ||
      payload.competitiveValue != null ||
      payload.volume != null
    );
  }

  private hasThreatLossFactorsData(
    payload: Partial<ThreatLossFactors>,
  ): boolean {
    return (
      !!payload.threatAction ||
      payload.threatCompetence != null ||
      !!payload.threatOrigin ||
      payload.tlfScore != null
    );
  }

  private hasOrganizationalLossFactorsData(
    payload: Partial<OrganizationalLossFactors>,
  ): boolean {
    return (
      payload.timingScore != null ||
      payload.careScore != null ||
      payload.detectionScore != null ||
      payload.containmentScore != null ||
      payload.remediationScore != null ||
      payload.recoveryScore != null ||
      payload.olfScore != null
    );
  }

  private hasExternalLossFactorsData(
    payload: Partial<ExternalLossFactors>,
  ): boolean {
    return (
      payload.externalDetection != null ||
      payload.regulatoryExposure != null ||
      payload.competitorScore != null ||
      payload.mediaScore != null ||
      payload.stakeholderScore != null ||
      payload.elfScore != null
    );
  }

  private hasLossMagnitudeData(payload: Partial<LossMagnitude>): boolean {
    return (
      payload.productivityLoss != null ||
      payload.responseCost != null ||
      payload.replacementCost != null ||
      payload.fines != null ||
      payload.competitiveLoss != null ||
      payload.reputationLoss != null ||
      payload.primaryLoss != null ||
      payload.secondaryLoss != null ||
      payload.totalLossMagnitude != null
    );
  }

  private parseOptionalNumber(value: string | null | undefined): number | undefined {
    if (value == null || `${value}`.trim() === "") {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  private registerTefCalculation(): void {
    this.stepTwoForm.controls.contactFrequency.valueChanges.subscribe(() => {
      this.updateTefResult();
    });

    this.stepTwoForm.controls.probabilityOfAction.valueChanges.subscribe(() => {
      this.updateTefResult();
    });
  }

  private updateTefResult(): void {
    const contactFrequency = this.parseOptionalNumber(
      this.stepTwoForm.controls.contactFrequency.value,
    );
    const probabilityOfAction = this.parseOptionalNumber(
      this.stepTwoForm.controls.probabilityOfAction.value,
    );

    const tefResult =
      contactFrequency != null && probabilityOfAction != null
        ? contactFrequency * probabilityOfAction
        : undefined;

    this.stepTwoForm.controls.tefResult.setValue(
      tefResult != null ? `${tefResult}` : "",
      { emitEvent: false },
    );

    this.updateLefResult();
  }

  private registerVulnerabilityCalculation(): void {
    this.stepThreeForm.controls.threatCapability.valueChanges.subscribe(() => {
      this.updateVulnerabilityResult();
    });

    this.stepThreeForm.controls.resistanceStrength.valueChanges.subscribe(() => {
      this.updateVulnerabilityResult();
    });
  }

  private updateVulnerabilityResult(): void {
    const threatCapability = this.parseOptionalNumber(
      this.stepThreeForm.controls.threatCapability.value,
    );
    const resistanceStrength = this.parseOptionalNumber(
      this.stepThreeForm.controls.resistanceStrength.value,
    );

    const denominator =
      (threatCapability ?? 0) + (resistanceStrength ?? 0);
    const vulnerabilityResult =
      threatCapability != null &&
      resistanceStrength != null &&
      denominator > 0
        ? threatCapability / denominator
        : undefined;

    this.stepThreeForm.controls.vulnerabilityResult.setValue(
      vulnerabilityResult != null ? `${vulnerabilityResult}` : "",
      { emitEvent: false },
    );

    this.updateLefResult();
  }

  private registerLefCalculation(): void {
    this.stepTwoForm.controls.tefResult.valueChanges.subscribe(() => {
      this.updateLefResult();
    });

    this.stepTwoForm.controls.timeframe.valueChanges.subscribe((timeframe) => {
      this.stepFourForm.controls.timeframe.setValue(timeframe ?? "", {
        emitEvent: false,
      });
    });

    this.stepThreeForm.controls.vulnerabilityResult.valueChanges.subscribe(() => {
      this.updateLefResult();
    });
  }

  private registerTlfCalculation(): void {
    this.stepSixForm.controls.threatAction.valueChanges.subscribe(() => {
      this.updateTlfResult();
    });

    this.stepSixForm.controls.threatCompetence.valueChanges.subscribe(() => {
      this.updateTlfResult();
    });

    this.stepSixForm.controls.threatOrigin.valueChanges.subscribe(() => {
      this.updateTlfResult();
    });
  }

  private registerOlfCalculation(): void {
    this.stepSevenForm.controls.timingScore.valueChanges.subscribe(() => {
      this.updateOlfResult();
    });
    this.stepSevenForm.controls.careScore.valueChanges.subscribe(() => {
      this.updateOlfResult();
    });
    this.stepSevenForm.controls.detectionScore.valueChanges.subscribe(() => {
      this.updateOlfResult();
    });
    this.stepSevenForm.controls.containmentScore.valueChanges.subscribe(() => {
      this.updateOlfResult();
    });
    this.stepSevenForm.controls.remediationScore.valueChanges.subscribe(() => {
      this.updateOlfResult();
    });
    this.stepSevenForm.controls.recoveryScore.valueChanges.subscribe(() => {
      this.updateOlfResult();
    });
  }

  private registerElfCalculation(): void {
    this.stepEightForm.controls.externalDetection.valueChanges.subscribe(() => {
      this.updateElfResult();
    });
    this.stepEightForm.controls.regulatoryExposure.valueChanges.subscribe(() => {
      this.updateElfResult();
    });
    this.stepEightForm.controls.competitorScore.valueChanges.subscribe(() => {
      this.updateElfResult();
    });
    this.stepEightForm.controls.mediaScore.valueChanges.subscribe(() => {
      this.updateElfResult();
    });
    this.stepEightForm.controls.stakeholderScore.valueChanges.subscribe(() => {
      this.updateElfResult();
    });
  }

  private registerLossMagnitudeCalculation(): void {
    this.stepNineForm.controls.productivityLoss.valueChanges.subscribe(() => {
      this.updateLossMagnitudeResult();
    });
    this.stepNineForm.controls.responseCost.valueChanges.subscribe(() => {
      this.updateLossMagnitudeResult();
    });
    this.stepNineForm.controls.replacementCost.valueChanges.subscribe(() => {
      this.updateLossMagnitudeResult();
    });
    this.stepNineForm.controls.fines.valueChanges.subscribe(() => {
      this.updateLossMagnitudeResult();
    });
    this.stepNineForm.controls.competitiveLoss.valueChanges.subscribe(() => {
      this.updateLossMagnitudeResult();
    });
    this.stepNineForm.controls.reputationLoss.valueChanges.subscribe(() => {
      this.updateLossMagnitudeResult();
    });
  }

  private registerRiskCalculation(): void {
    this.stepFourForm.controls.lefResult.valueChanges.subscribe(() => {
      this.updateRiskResult();
    });
    this.stepNineForm.controls.totalLossMagnitude.valueChanges.subscribe(() => {
      this.updateRiskResult();
    });
  }

  private updateLefResult(): void {
    const tef = this.parseOptionalNumber(this.stepTwoForm.controls.tefResult.value);
    const vulnerability = this.parseOptionalNumber(
      this.stepThreeForm.controls.vulnerabilityResult.value,
    );
    const lefResult =
      tef != null && vulnerability != null ? tef * vulnerability : undefined;

    this.stepFourForm.controls.tef.setValue(tef != null ? `${tef}` : "", {
      emitEvent: false,
    });
    this.stepFourForm.controls.vulnerability.setValue(
      vulnerability != null ? `${vulnerability}` : "",
      { emitEvent: false },
    );
    this.stepFourForm.controls.timeframe.setValue(
      this.stepTwoForm.controls.timeframe.value ?? "",
      { emitEvent: false },
    );
    this.stepFourForm.controls.lefResult.setValue(
      lefResult != null ? `${lefResult}` : "",
      { emitEvent: false },
    );
  }

  private updateTlfResult(): void {
    const threatAction = this.stepSixForm.controls.threatAction.value ?? "";
    const threatCompetence = this.parseOptionalNumber(
      this.stepSixForm.controls.threatCompetence.value,
    );
    const threatOrigin = this.stepSixForm.controls.threatOrigin.value ?? "";

    const actionWeight = this.threatActionWeights[threatAction];
    const originMultiplier = this.threatOriginMultipliers[threatOrigin];

    const tlfScore =
      actionWeight != null &&
      threatCompetence != null &&
      originMultiplier != null
        ? actionWeight * threatCompetence * originMultiplier
        : undefined;

    this.stepSixForm.controls.tlfScore.setValue(
      tlfScore != null ? `${tlfScore}` : "",
      { emitEvent: false },
    );
  }

  private updateOlfResult(): void {
    const timingScore = this.parseOptionalNumber(
      this.stepSevenForm.controls.timingScore.value,
    );
    const careScore = this.parseOptionalNumber(
      this.stepSevenForm.controls.careScore.value,
    );
    const detectionScore = this.parseOptionalNumber(
      this.stepSevenForm.controls.detectionScore.value,
    );
    const containmentScore = this.parseOptionalNumber(
      this.stepSevenForm.controls.containmentScore.value,
    );
    const remediationScore = this.parseOptionalNumber(
      this.stepSevenForm.controls.remediationScore.value,
    );
    const recoveryScore = this.parseOptionalNumber(
      this.stepSevenForm.controls.recoveryScore.value,
    );

    const responseModifier =
      containmentScore != null &&
      remediationScore != null &&
      recoveryScore != null
        ? (containmentScore + remediationScore + recoveryScore) / 3
        : undefined;

    const olfScore =
      timingScore != null &&
      careScore != null &&
      detectionScore != null &&
      responseModifier != null
        ? timingScore * careScore * detectionScore * responseModifier
        : undefined;

    this.stepSevenForm.controls.olfScore.setValue(
      olfScore != null ? `${olfScore}` : "",
      { emitEvent: false },
    );
  }

  private updateElfResult(): void {
    const externalDetection = this.parseOptionalNumber(
      this.stepEightForm.controls.externalDetection.value,
    );
    const regulatoryExposure = this.parseOptionalNumber(
      this.stepEightForm.controls.regulatoryExposure.value,
    );
    const competitorScore = this.parseOptionalNumber(
      this.stepEightForm.controls.competitorScore.value,
    );
    const mediaScore = this.parseOptionalNumber(
      this.stepEightForm.controls.mediaScore.value,
    );
    const stakeholderScore = this.parseOptionalNumber(
      this.stepEightForm.controls.stakeholderScore.value,
    );

    const elfScore =
      externalDetection != null &&
      regulatoryExposure != null &&
      competitorScore != null &&
      mediaScore != null &&
      stakeholderScore != null
        ? (
            externalDetection +
            regulatoryExposure +
            competitorScore +
            mediaScore +
            stakeholderScore
          ) / 5
        : undefined;

    this.stepEightForm.controls.elfScore.setValue(
      elfScore != null ? `${elfScore}` : "",
      { emitEvent: false },
    );
  }

  private updateLossMagnitudeResult(): void {
    const productivityLoss = this.parseOptionalNumber(
      this.stepNineForm.controls.productivityLoss.value,
    );
    const responseCost = this.parseOptionalNumber(
      this.stepNineForm.controls.responseCost.value,
    );
    const replacementCost = this.parseOptionalNumber(
      this.stepNineForm.controls.replacementCost.value,
    );
    const fines = this.parseOptionalNumber(this.stepNineForm.controls.fines.value);
    const competitiveLoss = this.parseOptionalNumber(
      this.stepNineForm.controls.competitiveLoss.value,
    );
    const reputationLoss = this.parseOptionalNumber(
      this.stepNineForm.controls.reputationLoss.value,
    );

    const primaryLoss =
      productivityLoss != null && responseCost != null && replacementCost != null
        ? productivityLoss + responseCost + replacementCost
        : undefined;
    const secondaryLoss =
      fines != null && competitiveLoss != null && reputationLoss != null
        ? fines + competitiveLoss + reputationLoss
        : undefined;
    const totalLossMagnitude =
      primaryLoss != null && secondaryLoss != null
        ? primaryLoss + secondaryLoss
        : undefined;

    this.stepNineForm.controls.primaryLoss.setValue(
      primaryLoss != null ? `${primaryLoss}` : "",
      { emitEvent: false },
    );
    this.stepNineForm.controls.secondaryLoss.setValue(
      secondaryLoss != null ? `${secondaryLoss}` : "",
      { emitEvent: false },
    );
    this.stepNineForm.controls.totalLossMagnitude.setValue(
      totalLossMagnitude != null ? `${totalLossMagnitude}` : "",
      { emitEvent: false },
    );

    this.updateRiskResult();
  }

  private updateRiskResult(): void {
    const lef = this.parseOptionalNumber(this.stepFourForm.controls.lefResult.value);
    const lossMagnitude = this.parseOptionalNumber(
      this.stepNineForm.controls.totalLossMagnitude.value,
    );
    const risk =
      lef != null && lossMagnitude != null ? lef * lossMagnitude : undefined;

    this.stepTenForm.controls.lef.setValue(lef != null ? `${lef}` : "", {
      emitEvent: false,
    });
    this.stepTenForm.controls.lossMagnitude.setValue(
      lossMagnitude != null ? `${lossMagnitude}` : "",
      { emitEvent: false },
    );
    this.stepTenForm.controls.risk.setValue(risk != null ? `${risk}` : "", {
      emitEvent: false,
    });
  }

  cancel(): void {
    this.router.navigate([
      "insight-egrc/auth-tools/risk-management/risk-register-search",
    ]);
  }

  openSuccessDialog(isCreate: boolean): void {
    this.dialog
      .open(SuccessDialogComponent, {
        width: "250px",
        data: { isCreate, title: "Risk Assessment" },
      })
      .afterClosed()
      .subscribe(() => {
        this.router.navigate([
          "insight-egrc/auth-tools/risk-management/risk-register-search",
        ]);
      });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage },
    });
  }
}
